/**
 * The four systemic cards above the fleet table.
 *
 * These count DERIVED STATES over the WHOLE fleet — never over the page the table happens
 * to show. That is why they come from their own endpoint (`/summary`) instead of being
 * tallied from the loaded rows: a count taken from a display sample is a wrong count, and
 * on a 57k-product site the visible page is 0.09% of the fleet.
 *
 * Each card prints the predicate the backend actually counted, straight from the API.
 */
import React from 'react';
import type { FleetSummary, SummaryCard } from '@/services/productCockpitService';
import { fmtInt, sinceLabel } from '../lib/stages';

interface SystemicStripProps {
  summary: FleetSummary | null;
  loading: boolean;
  error: string | null;
  /** drill into the products a card counted, where the filter can express it exactly */
  onDrill?: (card: SummaryCard) => void;
  /** key of the card whose drill-down filter is currently applied */
  activeKey?: string | null;
}

function tone(card: SummaryCard): string {
  if (card.tone === 'good') return 'pmc-good';
  if (!card.of) return '';
  return card.value > card.of / 2 ? 'pmc-poor' : 'pmc-weak';
}

export function SystemicStrip({ summary, loading, error, onDrill, activeKey = null }: SystemicStripProps) {
  if (error) {
    return (
      <div className="pmc-error" style={{ marginBottom: 18 }}>
        <div className="pmc-error-title">The fleet-wide summary could not be computed</div>
        <div className="pmc-error-msg">{error}</div>
        <div className="pmc-small pmc-mute" style={{ marginTop: 6 }}>
          The table below is unaffected — it is a separate query.
        </div>
      </div>
    );
  }

  if (loading || !summary) {
    return (
      <div className="pmc-sysrow">
        {[0, 1, 2, 3].map((i) => (
          <div className="pmc-syscard" key={i} aria-busy="true">
            <div className="pmc-skel" style={{ width: '55%', height: 20 }} />
            <div className="pmc-skel" style={{ width: '85%', marginTop: 10 }} />
            <div className="pmc-skel" style={{ width: '70%', marginTop: 8, height: 9 }} />
          </div>
        ))}
      </div>
    );
  }

  // ⛔ A ZERO IS A CLAIM TOO, AND IT NEEDS THE SAME QUALIFIER. The per-card number only
  // discloses an unresolved key when those products actually ENTER a problem count — and a
  // key the resolver could not produce often finds no dictionary rows and no benefit pool at
  // all, so they enter neither, and the two category-keyed cards then show unqualified
  // zeros. "0 of 126 lack definitions" reads as a clean bill of health for products whose
  // dictionary was never looked up under a key that exists.
  // the wider set: the resolver could not produce a key, OR the product has no knowledge-base
  // entry and generation has never resolved it at all — either way, not generation's key
  const notGenerations = summary.products_with_key_not_generations ?? summary.products_with_unresolved_category_key ?? 0;
  const unresolvedTotal = summary.products_with_unresolved_category_key ?? 0;
  const reasons = summary.unresolved_category_key_reasons ?? null;

  return (
    <>
      {notGenerations > 0 && (
        <div className="pmc-small pmc-warn-text" style={{ margin: '0 0 10px' }} role="status">
          ⚠ {fmtInt(notGenerations)} of {fmtInt(summary.total)} products are keyed on a category
          that is NOT the one generation used
          {unresolvedTotal > 0
            ? ` — ${fmtInt(unresolvedTotal)} because the resolver could not produce a key${reasons ? ` (${Object.entries(reasons).map(([k, n]) => `${k}: ${fmtInt(n)}`).join(', ')})` : ''}`
            : ''}
          {notGenerations - unresolvedTotal > 0
            ? `${unresolvedTotal > 0 ? ',' : ' —'} ${fmtInt(notGenerations - unresolvedTotal)} because they have no knowledge-base entry and generation has never resolved them`
            : ''}
          . The INTERPRET and MATCH cards below were computed with those keys, so their
          numbers hold for these products only if the key happened to be right — a ZERO
          included.
        </div>
      )}
      <div className="pmc-sysrow">
        {summary.cards.map((card) => {
          const drillable = !!onDrill && card.key === 'never_generated';
          const Tag = drillable ? 'button' : 'div';
          return (
            <Tag
              key={card.key}
              className={`pmc-syscard${drillable ? ' pmc-clickable' : ''}${activeKey === card.key ? ' pmc-active' : ''}`}
              {...(drillable
                ? {
                  type: 'button' as const,
                  onClick: () => onDrill!(card),
                  'aria-pressed': activeKey === card.key,
                  'data-tip': activeKey === card.key
                    ? 'Showing exactly these products — click to clear'
                    : 'Show exactly the products this card counted',
                }
                : {})}
            >
              <div className={`pmc-sv ${tone(card)}`}>
                {fmtInt(card.value)} of {fmtInt(card.of)}
              </div>
              <div className="pmc-sl">{card.label}</div>
              {/*
                ⛔ THE NUMBER AND WHAT QUALIFIES IT TRAVEL TOGETHER. These two cards count a
                CATEGORY-keyed fact — dictionary coverage, the cross-sector pool — and when
                the resolver could not produce a key the cockpit used the feed category
                unchanged. Those products were counted here anyway; the API says how many,
                and a count that cannot say so is a count nobody can act on.
              */}
              {!!card.unresolved_category_key && (
                <div className="pmc-predicate pmc-warn-text">
                  ⚠ {fmtInt(card.unresolved_category_key)} of these have an UNRESOLVED category key
                  {card.qualified_by ? ` — ${card.qualified_by}` : ''}
                </div>
              )}
              <div className="pmc-predicate">predicate: {card.predicate}</div>
            </Tag>
          );
        })}
      </div>
      <div className="pmc-small pmc-faint" style={{ margin: '-8px 0 18px' }}>
        Fleet-wide over {fmtInt(summary.total)} canonical active products
        {' · '}computed {sinceLabel(summary.computed_at)}
        {summary.cached ? ' (cached, 10 min)' : ` in ${(summary._meta.computed_ms / 1000).toFixed(1)} s`}
        {' · '}source {summary._meta.source}
      </div>
    </>
  );
}
